import React, { useEffect, useState } from 'react';
import { getEnquiries, createEnquiry, updateEnquiry, getCompanies, getAssets, checkFleetAvailability, createPurchaseRequest, getMyInfo, getMyScopedEnquiries, createQuotationFromEnquiry } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { FileText, Plus, CheckCircle2, Clock, Send, XCircle, Edit, MoreVertical, Eye, Phone, Mail, User, MapPin, Building, AlertTriangle, Truck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { GregorianDatePicker } from '../components/GregorianDatePicker';

const statusConfig = {
  new: { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'New' },
  in_progress: { icon: Clock, color: 'bg-amber-100 text-amber-800', label: 'In Progress' },
  quotation_sent: { icon: Send, color: 'bg-purple-100 text-purple-800', label: 'Quotation Sent' },
  won: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Won' },
  lost: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Lost' },
  pending_purchase: { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'Pending Purchase Rates' },
  rates_received: { icon: CheckCircle2, color: 'bg-teal-100 text-teal-800', label: 'Rates Received' },
};

// Enquiry Source options (per new spec)
const enquirySourceOptions = [
  { value: 'client_email', label: 'Client Email' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'sales_person_verbal', label: 'Sales Person (Verbal)' },
  { value: 'sales_person_email', label: 'Sales Person (Email)' }
];

// Requirement Type options (per new spec)
const requirementTypeOptions = [
  { value: 'equipment', label: 'Equipment Rental' },
  { value: 'manpower', label: 'Manpower Supply' },
  { value: 'material', label: 'Material Supply' },
  { value: 'transportation', label: 'Transportation' }
];

const emptyFormData = {
  company_id: '',
  client_name: '',
  client_email: '',
  client_phone: '',
  contact_person: '',
  project_name: '',
  project_location: '',
  enquiry_source: '',
  requirement_type: '',
  requirement: '',
  asset_type: '',
  with_driver: false,
  quantity: 1,
  start_date: '',
  duration_months: 1,
  assigned_sales_person: '',
  notes: ''
};

export const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [availableAssets, setAvailableAssets] = useState(0);
  const [sourcingInfo, setSourcingInfo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [useScopedData, setUseScopedData] = useState(false);
  // Send Quotation dialog
  const [sendQuoteOpen, setSendQuoteOpen] = useState(false);
  const [markup, setMarkup] = useState(15);
  const [rateBasis, setRateBasis] = useState('monthly');
  const [manualRate, setManualRate] = useState('');
  const [sendingQuote, setSendingQuote] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState(emptyFormData);

  useEffect(() => {
    loadCompanies();
    loadEnquiries();
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (formData.company_id && formData.asset_type && formData.quantity > 0) {
      checkAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.company_id, formData.asset_type, formData.quantity]);

  const loadCurrentUser = async () => {
    try {
      const response = await getMyInfo();
      setCurrentUser(response.data);
      // If user is sales-only, use scoped data
      if (response.data.is_sales_only) {
        setUseScopedData(true);
      }
    } catch (error) {
      console.error('Failed to load user info:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      // Use scoped data if user has limited access
      let response;
      if (useScopedData) {
        response = await getMyScopedEnquiries();
      } else {
        response = await getEnquiries();
      }
      setEnquiries(response.data);
    } catch (error) {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    try {
      // Use the new workflow endpoint to check fleet availability
      const response = await checkFleetAvailability(formData.company_id, formData.asset_type, formData.quantity);
      setSourcingInfo(response.data);
      setAvailableAssets(response.data.fleet_available);
    } catch (error) {
      console.error('Failed to check availability:', error);
      // Fallback to legacy method
      try {
        const response = await getAssets(formData.company_id, 'available');
        const matchingAssets = response.data.filter(a => 
          a.asset_type.toLowerCase().includes(formData.asset_type.toLowerCase())
        );
        setAvailableAssets(matchingAssets.length);
        setSourcingInfo(null);
      } catch (e) {
        console.error('Fallback check failed:', e);
      }
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setSelectedEnquiry(null);
    // Pre-fill salesperson name if we have current user info
    const prefilled = { ...emptyFormData };
    if (currentUser) {
      // Use full_name for display, but store user_id for reference
      prefilled.assigned_sales_person = currentUser.full_name || currentUser.user_id;
      // For sales users, auto-assign their operating company (they cannot pick).
      if (currentUser.is_sales_only && currentUser.company_ids && currentUser.company_ids.length > 0) {
        prefilled.company_id = currentUser.company_ids[0];
      }
    }
    setFormData(prefilled);
    setSourcingInfo(null);
    setDialogOpen(true);
  };

  const handleEdit = (enquiry) => {
    setIsEditing(true);
    setSelectedEnquiry(enquiry);
    setFormData({
      company_id: enquiry.company_id,
      client_name: enquiry.client_name,
      client_email: enquiry.client_email || '',
      client_phone: enquiry.client_phone || '',
      contact_person: enquiry.contact_person || '',
      project_name: enquiry.project_name || '',
      project_location: enquiry.project_location || '',
      enquiry_source: enquiry.enquiry_source || '',
      requirement_type: enquiry.requirement_type || '',
      requirement: enquiry.requirement,
      asset_type: enquiry.asset_type,
      with_driver: enquiry.with_driver,
      quantity: enquiry.quantity,
      start_date: enquiry.start_date || '',
      duration_months: enquiry.duration_months || 1,
      assigned_sales_person: enquiry.assigned_sales_person || '',
      notes: enquiry.notes || ''
    });
    setSourcingInfo(null);
    setDialogOpen(true);
  };

  const handleView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setViewDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedEnquiry) {
        await updateEnquiry(selectedEnquiry.id, formData);
        toast.success('Enquiry updated successfully!');
      } else {
        const response = await createEnquiry(formData);
        
        // Check if supplier sourcing is needed and auto-create purchase request
        if (sourcingInfo && sourcingInfo.requires_purchase_request) {
          try {
            await createPurchaseRequest({
              enquiry_id: response.data.id,
              asset_type: formData.asset_type,
              quantity: sourcingInfo.supplier_required,
              with_operator: formData.with_driver,
              duration_months: formData.duration_months,
              start_date: formData.start_date,
              notes: `Auto-generated from enquiry for ${formData.client_name}`
            });
            toast.success('Enquiry created! Purchase request sent to procurement team.');
          } catch (prError) {
            console.error('Failed to create purchase request:', prError);
            toast.success('Enquiry created! Note: Purchase request creation failed.');
          }
        } else {
          toast.success('Enquiry created successfully!');
        }
      }
      setDialogOpen(false);
      loadEnquiries();
      setFormData(emptyFormData);
      setSourcingInfo(null);
    } catch (error) {
      toast.error(isEditing ? 'Failed to update enquiry' : 'Failed to create enquiry');
    }
  };

  const openSendQuotation = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setMarkup(15);
    setRateBasis('monthly');
    setManualRate('');
    setSendQuoteOpen(true);
  };

  const handleSendQuotation = async () => {
    if (!selectedEnquiry) return;
    setSendingQuote(true);
    try {
      const payload = { markup_percent: markup, rate_basis: rateBasis, send: true };
      if (manualRate) payload.rate_per_unit = parseFloat(manualRate);
      const res = await createQuotationFromEnquiry(selectedEnquiry.id, payload);
      toast.success(`Quotation ${res.data.quotation.quotation_number} sent to ${selectedEnquiry.client_name}`);
      setSendQuoteOpen(false);
      setViewDialogOpen(false);
      loadEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quotation');
    } finally {
      setSendingQuote(false);
    }
  };

  const handleCreateQuotation = (enquiry) => {
    navigate('/quotations', { state: { enquiry } });
  };

  return (
    <div className="space-y-6" data-testid="enquiries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="enquiries-title">Sales Enquiries</h1>
          <p className="text-muted-foreground mt-1">{enquiries.length} total enquiries</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-enquiry-button">
          <Plus className="h-4 w-4 mr-2" />
          New Enquiry
        </Button>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Enquiry' : 'Create New Enquiry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Operating Company & Enquiry Source - Row 1 */}
            <div className={currentUser?.is_sales_only ? '' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
              {!currentUser?.is_sales_only && (
                <div>
                  <Label>Operating Company *</Label>
                  <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })} required disabled={isEditing}>
                    <SelectTrigger data-testid="enquiry-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Enquiry Source *</Label>
                <Select value={formData.enquiry_source} onValueChange={(v) => setFormData({ ...formData, enquiry_source: v })} required>
                  <SelectTrigger data-testid="enquiry-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {enquirySourceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {currentUser?.is_sales_only && (
              <p className="text-xs text-muted-foreground -mt-2" data-testid="sales-company-hint">
                Enquiry will be created under: <span className="font-semibold">
                  {companies.find(c => c.id === formData.company_id)?.name || 'your operating company'}
                </span>
              </p>
            )}

            {/* Client Name & Contact Person - Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Client Name *</Label>
                <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} required data-testid="client-name-input" />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} data-testid="contact-person-input" placeholder="Name of contact" />
              </div>
            </div>

            {/* Client Email & Phone - Row 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Client Email</Label>
                <Input type="email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} data-testid="client-email-input" />
              </div>
              <div>
                <Label>Client Phone</Label>
                <Input value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} data-testid="client-phone-input" />
              </div>
            </div>

            {/* Project Name & Location - NEW */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  Project Name
                </Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} data-testid="project-name-input" placeholder="Project or site name" />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Project Location
                </Label>
                <Input value={formData.project_location} onChange={(e) => setFormData({ ...formData, project_location: e.target.value })} data-testid="project-location-input" placeholder="Site address or city" />
              </div>
            </div>

            {/* Requirement Type & Asset Type - Row 4 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Requirement Type *</Label>
                <Select value={formData.requirement_type} onValueChange={(v) => setFormData({ ...formData, requirement_type: v })} required>
                  <SelectTrigger data-testid="requirement-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {requirementTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Asset/Service Type *</Label>
                <Input value={formData.asset_type} onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })} required data-testid="asset-type-input" placeholder="Pickup, Excavator, Welder..." />
                {/* Sourcing Status Display */}
                {sourcingInfo && (
                  <div className="mt-2 p-2 rounded border bg-slate-50 text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <Truck className="h-3 w-3" />
                      <span className="font-medium">Sourcing Analysis:</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Fleet Available:</span>
                      <span className={sourcingInfo.fleet_available >= formData.quantity ? 'text-green-600 font-medium' : 'text-amber-600'}>
                        {sourcingInfo.fleet_available} units
                      </span>
                    </div>
                    {sourcingInfo.sourcing_type === 'own_fleet' && (
                      <Badge className="bg-green-100 text-green-800 text-xs">✓ Can fulfill from Own Fleet</Badge>
                    )}
                    {sourcingInfo.sourcing_type === 'mixed' && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Need {sourcingInfo.supplier_required} from Supplier
                      </Badge>
                    )}
                    {sourcingInfo.sourcing_type === 'supplier_sourced' && (
                      <Badge className="bg-orange-100 text-orange-800 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Requires Supplier Sourcing
                      </Badge>
                    )}
                  </div>
                )}
                {!sourcingInfo && availableAssets > 0 && (
                  <p className="text-xs text-green-600 mt-1">{availableAssets} available in fleet</p>
                )}
              </div>
            </div>

            {/* Requirement Description */}
            <div>
              <Label>Requirement Details *</Label>
              <Textarea value={formData.requirement} onChange={(e) => setFormData({ ...formData, requirement: e.target.value })} required data-testid="requirement-input" placeholder="Describe the client's requirements in detail" rows={3} />
            </div>

            {/* Quantity, Duration, Start Date - Row 5 */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} required data-testid="quantity-input" />
              </div>
              <div>
                <Label>Duration (months) *</Label>
                <Input type="number" min="1" value={formData.duration_months} onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || 1 })} required data-testid="duration-input" />
              </div>
              <div>
                <Label>Expected Start Date</Label>
                <GregorianDatePicker
                  value={formData.start_date}
                  onChange={(iso) => setFormData({ ...formData, start_date: iso })}
                  testid="start-date-input"
                  placeholder="Pick a start date"
                />
              </div>
            </div>

            {/* With Driver & Assigned Sales Person - Row 6 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="with_driver" checked={formData.with_driver} onChange={(e) => setFormData({ ...formData, with_driver: e.target.checked })} data-testid="with-driver-checkbox" />
                <Label htmlFor="with_driver">With Driver/Operator</Label>
              </div>
              <div>
                <Label>Assigned Sales Person</Label>
                <Input value={formData.assigned_sales_person} onChange={(e) => setFormData({ ...formData, assigned_sales_person: e.target.value })} data-testid="assigned-sales-input" placeholder="Sales person name" />
              </div>
            </div>

            {/* Internal Notes */}
            <div>
              <Label>Internal Notes</Label>
              <Textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes..." rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">Cancel</Button>
              <Button type="submit" data-testid="submit-enquiry-button">
                {isEditing ? 'Update Enquiry' : 'Create Enquiry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
          </DialogHeader>
          
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold">{selectedEnquiry.client_name}</h3>
                  {selectedEnquiry.client_email && (
                    <p className="text-sm text-muted-foreground">{selectedEnquiry.client_email}</p>
                  )}
                  {selectedEnquiry.client_phone && (
                    <p className="text-sm text-muted-foreground">{selectedEnquiry.client_phone}</p>
                  )}
                </div>
                <Badge className={statusConfig[selectedEnquiry.status]?.color}>
                  {statusConfig[selectedEnquiry.status]?.label}
                </Badge>
              </div>

              {/* Project Info */}
              {(selectedEnquiry.project_name || selectedEnquiry.project_location) && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                  {selectedEnquiry.project_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{selectedEnquiry.project_name}</span>
                    </div>
                  )}
                  {selectedEnquiry.project_location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedEnquiry.project_location}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Sourcing Info */}
              {selectedEnquiry.sourcing_type && (
                <div className="border-l-4 border-amber-400 pl-3 py-2 bg-amber-50 rounded-r">
                  <p className="text-sm font-medium">Sourcing: {selectedEnquiry.sourcing_type.replace('_', ' ')}</p>
                  {selectedEnquiry.supplier_required > 0 && (
                    <p className="text-xs text-amber-700">
                      {selectedEnquiry.supplier_required} units from supplier required
                    </p>
                  )}
                </div>
              )}

              {/* Supplier Rates - visible after Purchase Dept has responded */}
              {Array.isArray(selectedEnquiry.supplier_rates) && selectedEnquiry.supplier_rates.length > 0 && (
                <div className="border-l-4 border-teal-500 bg-teal-50 rounded-r p-3" data-testid="enquiry-supplier-rates">
                  <p className="text-sm font-semibold text-teal-800 mb-2">
                    Sourcing price from Purchase Dept
                  </p>
                  <div className="space-y-2">
                    {selectedEnquiry.supplier_rates.map((r, idx) => (
                      <div key={idx} className="text-xs bg-white rounded p-2 border border-teal-100">
                        <p className="font-semibold">{r.supplier_name || 'Supplier'}</p>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          {r.daily_rate ? <div><span className="text-muted-foreground">Daily:</span> SAR {Number(r.daily_rate).toLocaleString()}</div> : null}
                          {r.monthly_rate ? <div><span className="text-muted-foreground">Monthly:</span> SAR {Number(r.monthly_rate).toLocaleString()}</div> : null}
                          {r.mobilization ? <div><span className="text-muted-foreground">Mobilization:</span> SAR {Number(r.mobilization).toLocaleString()}</div> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Asset Type</p>
                  <p className="font-medium">{selectedEnquiry.asset_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Quantity</p>
                  <p className="font-medium">{selectedEnquiry.quantity}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="font-medium">{selectedEnquiry.duration_months} months</p>
                </div>
                <div>
                  <p className="text-muted-foreground">With Driver</p>
                  <p className="font-medium">{selectedEnquiry.with_driver ? 'Yes' : 'No'}</p>
                </div>
                {selectedEnquiry.start_date && (
                  <div>
                    <p className="text-muted-foreground">Start Date</p>
                    <p className="font-medium">{new Date(selectedEnquiry.start_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-muted-foreground text-sm">Requirement</p>
                <p className="mt-1">{selectedEnquiry.requirement}</p>
              </div>

              {selectedEnquiry.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="mt-1 text-sm">{selectedEnquiry.notes}</p>
                </div>
              )}

              <div className="text-xs text-muted-foreground border-t pt-3">
                Created: {new Date(selectedEnquiry.created_at).toLocaleString()}
              </div>

              {/* Actions - Send Quotation directly from enquiry */}
              {selectedEnquiry.status !== 'quotation_sent' && selectedEnquiry.status !== 'won' && selectedEnquiry.status !== 'lost' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setViewDialogOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => openSendQuotation(selectedEnquiry)}
                    data-testid="send-quotation-from-enquiry"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Quotation
                  </Button>
                </div>
              )}
              {selectedEnquiry.status === 'quotation_sent' && selectedEnquiry.quotation_number && (
                <div className="text-xs text-green-700 border-t pt-3">
                  Quotation <span className="font-semibold">{selectedEnquiry.quotation_number}</span> already sent.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Quotation Dialog */}
      <Dialog open={sendQuoteOpen} onOpenChange={setSendQuoteOpen}>
        <DialogContent className="max-w-md" data-testid="send-quotation-dialog">
          <DialogHeader>
            <DialogTitle>Send Quotation</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 p-3 rounded">
                <p><span className="text-muted-foreground">Client:</span> <span className="font-medium">{selectedEnquiry.client_name}</span></p>
                <p><span className="text-muted-foreground">Item:</span> {selectedEnquiry.quantity}x {selectedEnquiry.asset_type} for {selectedEnquiry.duration_months} mo</p>
              </div>

              {Array.isArray(selectedEnquiry.supplier_rates) && selectedEnquiry.supplier_rates.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Rate basis</Label>
                      <Select value={rateBasis} onValueChange={setRateBasis}>
                        <SelectTrigger data-testid="rate-basis-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Markup %</Label>
                      <Input
                        type="number"
                        min="0"
                        value={markup}
                        onChange={(e) => setMarkup(parseFloat(e.target.value) || 0)}
                        data-testid="markup-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Rate = supplier best rate × (1 + markup%). Manual rate below overrides this.
                  </p>
                </>
              ) : (
                <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  No supplier rate available yet. Enter a manual rate below.
                </div>
              )}
              <div>
                <Label>Manual rate per unit (SAR, optional)</Label>
                <Input
                  type="number"
                  value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)}
                  placeholder="Leave empty to use supplier + markup"
                  data-testid="manual-rate-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendQuoteOpen(false)} disabled={sendingQuote}>Cancel</Button>
            <Button onClick={handleSendQuotation} disabled={sendingQuote} data-testid="confirm-send-quotation">
              {sendingQuote ? 'Sending...' : 'Create & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading enquiries...</div>
        </div>
      ) : enquiries.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No enquiries yet</h3>
          <p className="text-muted-foreground mb-4">Start tracking your sales pipeline</p>
          <Button onClick={handleCreate}>
            Create First Enquiry
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enquiries.map((enquiry) => {
            const StatusIcon = statusConfig[enquiry.status]?.icon || Clock;
            return (
              <Card key={enquiry.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`enquiry-card-${enquiry.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid={`enquiry-client-${enquiry.id}`}>{enquiry.client_name}</h3>
                      <p className="text-sm text-muted-foreground">{enquiry.asset_type}</p>
                    </div>
                  </div>
                  <Badge className={statusConfig[enquiry.status]?.color} data-testid={`enquiry-status-${enquiry.id}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig[enquiry.status]?.label}
                  </Badge>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-semibold">{enquiry.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span>{enquiry.duration_months} months</span>
                  </div>
                  {enquiry.with_driver && (
                    <div className="text-xs text-blue-600">With Driver/Operator</div>
                  )}
                  {enquiry.project_name && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      {enquiry.project_name}
                    </div>
                  )}
                  {/* Sourcing Badge */}
                  {enquiry.sourcing_type === 'supplier_sourced' && (
                    <Badge className="bg-orange-100 text-orange-800 text-xs">Supplier Sourcing</Badge>
                  )}
                  {enquiry.sourcing_type === 'mixed' && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">Mixed Sourcing</Badge>
                  )}
                  {enquiry.requirement && (
                    <p className="text-xs text-muted-foreground pt-2 border-t">{enquiry.requirement.substring(0, 100)}...</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {(enquiry.status === 'new' || enquiry.status === 'rates_received' || enquiry.status === 'in_progress') && (
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => openSendQuotation(enquiry)}
                      data-testid={`send-quotation-${enquiry.id}`}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send Quotation
                    </Button>
                  )}
                  {(enquiry.status === 'quotation_sent' || enquiry.status === 'won' || enquiry.status === 'lost' || enquiry.status === 'pending_purchase') && (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(enquiry)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleView(enquiry)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      {(enquiry.status === 'new' || enquiry.status === 'in_progress') && (
                        <DropdownMenuItem onClick={() => handleEdit(enquiry)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Enquiry
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
